import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { Loader2, Globe, DollarSign, TrendingUp, TrendingDown, ArrowRightLeft, RefreshCw, Building2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';
import BranchLink from '@/components/BranchLink';
import ShareViewButton from '@/components/common/ShareViewButton';

const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

export default function CrossBranchReportPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/cross-branch-summary');
      setData(res.data);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="text-center py-12 text-muted-foreground">Failed to load report</div></DashboardLayout>;

  const { consolidated, branches, rates_updated_at } = data;
  const chartData = branches.map(b => ({
    name: b.branch_name,
    Sales: b.converted_sales,
    Expenses: b.converted_expenses,
    Profit: b.converted_profit,
  }));
  const pieData = branches.filter(b => b.converted_sales > 0).map(b => ({
    name: b.branch_name,
    value: b.converted_sales,
  }));

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="cross-branch-report">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200/50">

                <Building2 size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Cross Branch Report</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Multi-branch financial comparison</p>

              </div>

            </div>
            <p className="text-sm text-muted-foreground">{t('cross_branch_subtitle') || 'Cross-country branch comparison'} in {consolidated.primary_currency}</p>
          </div>
          <div className="flex items-center gap-2">
            {rates_updated_at && <span className="text-[10px] text-muted-foreground">Rates: {new Date(rates_updated_at).toLocaleDateString()}</span>}
            <ShareViewButton params={{}} />
            <Button size="sm" variant="outline" className="rounded-full" onClick={fetchReport} data-testid="refresh-report-btn"><RefreshCw size={14} className="mr-1" />Refresh</Button>
          </div>
        </div>

        {/* Consolidated KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-emerald-50 to-white border-success/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Sales</p>
                  <p className="text-2xl font-bold text-success" data-testid="consolidated-sales">{consolidated.primary_currency} {consolidated.total_sales.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <TrendingUp size={24} className="text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-white border-error/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Expenses</p>
                  <p className="text-2xl font-bold text-error" data-testid="consolidated-expenses">{consolidated.primary_currency} {consolidated.total_expenses.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <TrendingDown size={24} className="text-red-400" />
              </div>
            </CardContent>
          </Card>
          <Card className={`bg-gradient-to-br ${consolidated.net_profit >= 0 ? 'from-blue-50 to-white border-info/30' : 'from-orange-50 to-white border-orange-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Net Profit</p>
                  <p className={`text-2xl font-bold ${consolidated.net_profit >= 0 ? 'text-info' : 'text-orange-700'}`} data-testid="consolidated-profit">{consolidated.primary_currency} {consolidated.net_profit.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <DollarSign size={24} className={consolidated.net_profit >= 0 ? 'text-blue-400' : 'text-orange-400'} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-sm">Branch Comparison ({consolidated.primary_currency})</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Sales" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Profit" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-sm">Revenue Share by Branch</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${consolidated.primary_currency} ${v.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Branch Detail Table */}
        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><ArrowRightLeft size={16} />Branch-by-Branch Breakdown</CardTitle></CardHeader>
          <CardContent>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="branch-breakdown-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2">Branch</th>
                    <th className="text-left p-2">Country</th>
                    <th className="text-right p-2">Local Sales</th>
                    <th className="text-right p-2">Local Expenses</th>
                    <th className="text-center p-2">Rate</th>
                    <th className="text-right p-2">Converted Sales</th>
                    <th className="text-right p-2">Converted Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {branches.map((b, i) => (
                    <tr key={b.branch_id} className={i % 2 ? 'bg-muted/50' : ''}>
                      <td className="p-2 font-medium"><BranchLink branchId={b.branch_id} name={b.branch_name} /></td>
                      <td className="p-2"><Badge variant="outline" className="text-[10px]"><Globe size={10} className="mr-1" />{b.country_code || '-'}</Badge></td>
                      <td className="p-2 text-right text-success">{b.local_currency} {b.local_sales.toLocaleString()}</td>
                      <td className="p-2 text-right text-error">{b.local_currency} {b.local_expenses.toLocaleString()}</td>
                      <td className="p-2 text-center text-xs text-muted-foreground">{b.rate_to_primary === 1 ? '-' : `x${b.rate_to_primary}`}</td>
                      <td className="p-2 text-right font-medium">{consolidated.primary_currency} {b.converted_sales.toLocaleString()}</td>
                      <td className={`p-2 text-right font-bold ${b.converted_profit >= 0 ? 'text-success' : 'text-error'}`}>{consolidated.primary_currency} {b.converted_profit.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {branches.map(b => (
                <div key={b.branch_id} className="p-3 border rounded-lg space-y-2" data-testid="branch-breakdown-card">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-sm"><BranchLink branchId={b.branch_id} name={b.branch_name} /></span>
                    <Badge variant="outline" className="text-[10px]">{b.country_code || '-'} | {b.local_currency}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-muted-foreground">Local Sales:</span> <span className="font-medium text-success">{b.local_currency} {b.local_sales.toLocaleString()}</span></div>
                    <div><span className="text-muted-foreground">Local Expenses:</span> <span className="font-medium text-error">{b.local_currency} {b.local_expenses.toLocaleString()}</span></div>
                    <div><span className="text-muted-foreground">Converted:</span> <span className="font-medium">{consolidated.primary_currency} {b.converted_sales.toLocaleString()}</span></div>
                    <div><span className="text-muted-foreground">Profit:</span> <span className={`font-bold ${b.converted_profit >= 0 ? 'text-success' : 'text-error'}`}>{consolidated.primary_currency} {b.converted_profit.toLocaleString()}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
